import React, { useState, useEffect, useRef } from 'react';
import { 
  Mail, Plus, Edit2, Trash2, Copy, Eye, Send, 
  FileText, Clock, Users, X, Variable, Monitor, Smartphone
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const normalizeTemplateKey = (value) =>
  String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

const renderTemplatePreview = (text, sampleData) =>
  String(text || '').replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (match, token) => {
    if (sampleData[token] !== undefined) {
      return sampleData[token];
    }
    const normalizedKey = normalizeTemplateKey(token);
    return sampleData[normalizedKey] !== undefined ? sampleData[normalizedKey] : match;
  });

const extractTemplateVariables = (template) => {
  const fromList = Array.isArray(template?.variables) ? template.variables : [];
  const fromText = [];
  const source = `${template?.subject || ''}\n${template?.body || ''}`;
  const regex = /\{\{\s*([^{}]+?)\s*\}\}/g;
  let match;
  while ((match = regex.exec(source)) !== null) {
    const token = String(match[1] || '').trim();
    if (token) fromText.push(token);
  }
  return Array.from(new Set([...fromList, ...fromText]));
};

const getLayoutPreviewData = () => ({
  site_name: 'One Globe Alliance',
  support_email: 'info@oneglobealliance.com',
  current_year: new Date().getFullYear(),
  logo_url: 'https://oneglobealliance.com/wp-content/uploads/2026/03/footer-logo-D5II3NAL-removebg-preview.png'
});

const PREVIEW_DEVICES = {
  desktop: { width: 600, label: 'Desktop' },
  mobile: { width: 375, label: 'Mobile' }
};
const PREVIEW_ZOOM_OPTIONS = [20, 30, 40, 50, 67, 75, 90, 100, 110, 125, 150];
const DEFAULT_HEADER_BACKGROUND_COLOR = '#1a365d';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showLayoutDialog, setShowLayoutDialog] = useState(false);
  const [layoutEditMode, setLayoutEditMode] = useState('header');
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [previewZoomMode, setPreviewZoomMode] = useState('fit'); // fit | manual
  const [previewZoomPercent, setPreviewZoomPercent] = useState(100);
  const [previewViewportWidth, setPreviewViewportWidth] = useState(0);
  
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewContent, setPreviewContent] = useState({ subject: '', body: '' });
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [testVariables, setTestVariables] = useState({});
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [layoutSettings, setLayoutSettings] = useState({
    header_html: '',
    footer_html: '',
    header_background_color: DEFAULT_HEADER_BACKGROUND_COLOR
  });
  const [layoutForm, setLayoutForm] = useState({
    header_html: '',
    footer_html: '',
    header_background_color: DEFAULT_HEADER_BACKGROUND_COLOR
  });
  
  // Forms
  const [form, setForm] = useState({
    name: '',
    subject: '',
    body: '',
    template_type: 'general',
    variables: []
  });
  
  const [newVariable, setNewVariable] = useState('');
  const previewViewportRef = useRef(null);
  
  const { api, hasPermission, admin } = useAdminAuth();

  useEffect(() => {
    fetchTemplates();
    fetchLayoutSettings();
  }, []);

  useEffect(() => {
    if (!showLayoutDialog) return undefined;
    const el = previewViewportRef.current;
    if (!el) return undefined;

    const updateSize = () => setPreviewViewportWidth(el.clientWidth || 0);
    updateSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(el);
    return () => observer.disconnect();
  }, [showLayoutDialog]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/team/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchLayoutSettings = async () => {
    try {
      const response = await api.get('/api/admin/team/templates/layout');
      const layout = {
        header_html: response.data?.header_html || '',
        footer_html: response.data?.footer_html || '',
        header_background_color: response.data?.header_background_color || DEFAULT_HEADER_BACKGROUND_COLOR
      };
      setLayoutSettings(layout);
      setLayoutForm(layout);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to load template layout'));
    }
  };

  const handleCreate = async () => {
    if (!form.name || !form.subject || !form.body) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await api.post('/api/admin/team/templates', form);
      toast.success('Template created');
      setShowCreateDialog(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to create template'));
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/api/admin/team/templates/${selectedTemplate.template_id}`, form);
      toast.success('Template updated');
      setShowEditDialog(false);
      fetchTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update template'));
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await api.delete(`/api/admin/team/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to delete template'));
    }
  };

  const openLayoutDialog = () => {
    setLayoutEditMode('header');
    setLayoutForm({
      header_html: layoutSettings.header_html || '',
      footer_html: layoutSettings.footer_html || '',
      header_background_color: layoutSettings.header_background_color || DEFAULT_HEADER_BACKGROUND_COLOR
    });
    setShowLayoutDialog(true);
  };

  const handleLayoutSave = async () => {
    try {
      setLayoutSaving(true);
      await api.put('/api/admin/team/templates/layout', layoutForm);
      toast.success('Email layout updated');
      setLayoutSettings({ ...layoutForm });
      setShowLayoutDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update layout'));
    } finally {
      setLayoutSaving(false);
    }
  };

  const handleDuplicate = (template) => {
    setForm({
      name: `${template.name} (Copy)`,
      subject: template.subject,
      body: template.body,
      template_type: template.template_type,
      variables: template.variables || []
    });
    setShowCreateDialog(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      subject: '',
      body: '',
      template_type: 'general',
      variables: []
    });
    setNewVariable('');
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      template_type: template.template_type,
      variables: template.variables || []
    });
    setShowEditDialog(true);
  };

  const openPreviewDialog = (template) => {
    const sampleData = {
      'First Name': 'John',
      'Company Name': 'Global Logistics Corp',
      'Affiliate Link': 'https://oneglobealliance.com/affiliate',
      'Register Link': 'https://oneglobealliance.com/register',
      'Deck Link': 'https://oneglobealliance.com/deck',
      'Demo Link': 'https://oneglobealliance.com/book-demo',
      contact_name: 'John Doe',
      company_name: 'Global Logistics Corp',
      member_id: 'MEM-12345678',
      tier: 'Platinum',
      portal_link: 'https://portal.oneglobealliance.com',
      registration_link: 'https://portal.oneglobealliance.com/register',
      amount: '$999',
      due_date: 'March 15, 2026',
      calendar_link: 'https://calendly.com/oga',
      sender_name: 'Sarah Johnson'
    };

    setPreviewContent({
      subject: renderTemplatePreview(template.subject, sampleData),
      body: renderTemplatePreview(template.body, sampleData),
    });
    const vars = extractTemplateVariables(template);
    const initialVarValues = {};
    vars.forEach((varName) => {
      if (sampleData[varName] !== undefined) {
        initialVarValues[varName] = sampleData[varName];
        return;
      }
      const normalized = normalizeTemplateKey(varName);
      if (sampleData[normalized] !== undefined) {
        initialVarValues[varName] = sampleData[normalized];
        return;
      }
      initialVarValues[varName] = '';
    });
    setSelectedTemplate(template);
    setTestVariables(initialVarValues);
    setTestEmailRecipient(admin?.email || '');
    setShowPreviewDialog(true);
  };

  const handleSendTestEmail = async () => {
    if (!selectedTemplate?.template_id) return;
    if (!testEmailRecipient.trim()) {
      toast.error('Recipient email is required');
      return;
    }

    try {
      setSendingTestEmail(true);
      await api.post(
        `/api/admin/team/templates/${selectedTemplate.template_id}/send-test`,
        {
          email: testEmailRecipient.trim(),
          variables: testVariables,
        }
      );
      toast.success('Test email sent');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send test email'));
    } finally {
      setSendingTestEmail(false);
    }
  };

  const addVariable = () => {
    if (!newVariable.trim()) return;
    const varName = newVariable.trim();
    if (!form.variables.includes(varName)) {
      setForm({ ...form, variables: [...form.variables, varName] });
    }
    setNewVariable('');
  };

  const removeVariable = (varName) => {
    setForm({ ...form, variables: form.variables.filter(v => v !== varName) });
  };

  const insertVariable = (varName) => {
    setForm({ ...form, body: form.body + `{{${varName}}}` });
  };

  const getTypeBadge = (type) => {
    const badges = {
      invite: { bg: 'bg-purple-100', text: 'text-purple-700' },
      welcome: { bg: 'bg-green-100', text: 'text-green-700' },
      reminder: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      payment: { bg: 'bg-blue-100', text: 'text-blue-700' },
      follow_up: { bg: 'bg-orange-100', text: 'text-orange-700' },
      target_outreach: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
      bulk: { bg: 'bg-red-100', text: 'text-red-700' },
      general: { bg: 'bg-gray-100', text: 'text-gray-700' }
    };
    return badges[type] || badges.general;
  };

  const templateTypes = [
    { value: 'invite', label: 'Invitation' },
    { value: 'welcome', label: 'Welcome' },
    { value: 'reminder', label: 'Reminder' },
    { value: 'payment', label: 'Payment' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'target_outreach', label: 'Target Outreach' },
    { value: 'bulk', label: 'Bulk' },
    { value: 'general', label: 'General' }
  ];

  const filteredTemplates = activeTab === 'all' 
    ? templates 
    : templates.filter(t => t.template_type === activeTab);

  const previewData = getLayoutPreviewData();
  const previewHeaderHtml = renderTemplatePreview(layoutForm.header_html, previewData);
  const previewFooterHtml = renderTemplatePreview(layoutForm.footer_html, previewData);
  const previewWidth = PREVIEW_DEVICES[previewDevice]?.width || PREVIEW_DEVICES.desktop.width;
  const fitScale = previewViewportWidth > 0
    ? Math.max(0.2, Math.min(1.5, previewViewportWidth / previewWidth))
    : 1;
  const previewScale = previewZoomMode === 'fit'
    ? fitScale
    : Math.max(0.2, Math.min(1.5, previewZoomPercent / 100));
  const scaledPreviewWidth = Math.round(previewWidth * previewScale);
  const displayZoomPercent = Math.round(previewScale * 100);

  return (
    <div className="space-y-6" data-testid="email-templates">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-500">Create and manage email templates for communications</p>
        </div>
        {hasPermission('templates', 'create') && (
          <Button 
            onClick={() => { resetForm(); setShowCreateDialog(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="create-template-btn"
          >
            <Plus size={16} className="mr-2" />
            Create Template
          </Button>
        )}
      </div>

      {/* Stats */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Email Layout</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Update reusable HTML sections used above and below every email template body.
          </p>
          <div className="flex flex-wrap gap-2">
            {hasPermission('templates', 'edit') && (
              <Button variant="outline" onClick={openLayoutDialog}>
                <Edit2 size={14} className="mr-2" />
                Edit Layout
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Templates</p>
                <p className="text-2xl font-bold">{templates.length}</p>
              </div>
              <FileText size={24} className="text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Invitation</p>
                <p className="text-2xl font-bold">{templates.filter(t => t.template_type === 'invite').length}</p>
              </div>
              <Mail size={24} className="text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Payment Related</p>
                <p className="text-2xl font-bold">{templates.filter(t => t.template_type === 'payment' || t.template_type === 'reminder').length}</p>
              </div>
              <Clock size={24} className="text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Target Outreach</p>
                <p className="text-2xl font-bold">{templates.filter(t => t.template_type === 'target_outreach').length}</p>
              </div>
              <Users size={24} className="text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Sent</p>
                <p className="text-2xl font-bold">{templates.reduce((sum, t) => sum + (t.usage_count || 0), 0)}</p>
              </div>
              <Send size={24} className="text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({templates.length})</TabsTrigger>
          <TabsTrigger value="invite">Invitation</TabsTrigger>
          <TabsTrigger value="welcome">Welcome</TabsTrigger>
          <TabsTrigger value="reminder">Reminder</TabsTrigger>
          <TabsTrigger value="payment">Payment</TabsTrigger>
          <TabsTrigger value="follow_up">Follow Up</TabsTrigger>
          <TabsTrigger value="target_outreach">Target Outreach</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading templates...</div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Mail size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No templates found</p>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              variant="outline" 
              className="mt-4"
            >
              Create your first template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredTemplates.map((template) => {
            const typeBadge = getTypeBadge(template.template_type);
            return (
              <Card key={template.template_id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Mail size={18} className="text-blue-600" />
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                      </div>
                      <Badge className={`mt-2 ${typeBadge.bg} ${typeBadge.text}`}>
                        {template.template_type}
                      </Badge>
                    </div>
                    {template.usage_count > 0 && (
                      <Badge variant="outline" className="text-gray-500">
                        Used {template.usage_count}x
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Subject</p>
                    <p className="text-sm font-medium truncate">{template.subject}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Preview</p>
                    <p className="text-sm text-gray-600 line-clamp-2">{template.body}</p>
                  </div>
                  
                  {template.variables && template.variables.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {template.variables.slice(0, 4).map((v, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {`{{${v}}}`}
                        </Badge>
                      ))}
                      {template.variables.length > 4 && (
                        <Badge variant="outline" className="text-xs">
                          +{template.variables.length - 4} more
                        </Badge>
                      )}
                    </div>
                  )}
                  
                  <div className="flex space-x-2 pt-2 border-t">
                    <Button size="sm" variant="outline" onClick={() => openPreviewDialog(template)}>
                      <Eye size={14} className="mr-1" />
                      Preview
                    </Button>
                    {hasPermission('templates', 'edit') && (
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(template)}>
                        <Edit2 size={14} className="mr-1" />
                        Edit
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleDuplicate(template)}>
                      <Copy size={14} className="mr-1" />
                      Duplicate
                    </Button>
                    {hasPermission('templates', 'delete') && !template.template_id?.startsWith('default-') && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600"
                        onClick={() => handleDelete(template.template_id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEditDialog ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Welcome Email"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Type *</Label>
                <select
                  value={form.template_type}
                  onChange={(e) => setForm({ ...form, template_type: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                  {templateTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <Label>Subject Line *</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="e.g., Welcome to One Globe Alliance!"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Email Body *</Label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Dear {{contact_name}},&#10;&#10;Welcome to One Globe Alliance..."
                rows={10}
                className="mt-1 font-mono text-sm"
              />
            </div>
            
            <div className="border rounded-lg p-4 bg-gray-50">
              <Label className="flex items-center mb-2">
                <Variable size={16} className="mr-2" />
                Template Variables
              </Label>
              <p className="text-xs text-gray-500 mb-3">
                Use variables like {"{{variable_name}}"} in your template. They will be replaced with actual values when sending.
              </p>
              
              <div className="flex space-x-2 mb-3">
                <Input
                  value={newVariable}
                  onChange={(e) => setNewVariable(e.target.value)}
                  placeholder="Add variable name..."
                  onKeyPress={(e) => e.key === 'Enter' && addVariable()}
                />
                <Button type="button" onClick={addVariable} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {form.variables.map((v, idx) => (
                  <Badge key={idx} variant="secondary" className="cursor-pointer group">
                    <span onClick={() => insertVariable(v)}>
                      {`{{${v}}}`}
                    </span>
                    <button 
                      onClick={() => removeVariable(v)}
                      className="ml-1 opacity-50 hover:opacity-100"
                    >
                      <X size={12} />
                    </button>
                  </Badge>
                ))}
                {form.variables.length === 0 && (
                  <span className="text-xs text-gray-400">No variables added</span>
                )}
              </div>
              
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-gray-500 font-medium mb-2">Common Variables:</p>
                <div className="flex flex-wrap gap-1">
                  {['contact_name', 'company_name', 'member_id', 'tier', 'portal_link', 'amount', 'due_date'].map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        if (!form.variables.includes(v)) {
                          setForm({ ...form, variables: [...form.variables, v] });
                        }
                        insertVariable(v);
                      }}
                      className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-100"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => {
                setShowCreateDialog(false);
                setShowEditDialog(false);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={showEditDialog ? handleUpdate : handleCreate} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {showEditDialog ? 'Update Template' : 'Create Template'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Eye size={20} className="mr-2 text-blue-600" />
              Preview: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-100 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Subject</p>
              <p className="font-medium">{previewContent.subject}</p>
            </div>
            
            <div className="border rounded-lg p-4 bg-white">
              <p className="text-xs text-gray-500 uppercase font-medium mb-2">Email Body</p>
              <div className="whitespace-pre-wrap text-sm text-gray-700">
                {previewContent.body}
              </div>
            </div>
            
            <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-700">
              <p className="font-medium mb-1">Sample Data Preview</p>
              <p className="text-xs">Variables have been replaced with sample data for preview purposes.</p>
            </div>

            {hasPermission('templates', 'edit') && (
              <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Send Test Email</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Provide custom variable values and send this template to a test recipient.
                  </p>
                </div>
                <div>
                  <Label>Recipient Email</Label>
                  <Input
                    value={testEmailRecipient}
                    onChange={(e) => setTestEmailRecipient(e.target.value)}
                    placeholder="name@example.com"
                    className="mt-1"
                  />
                </div>
                {Object.keys(testVariables).length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Template Variables</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(testVariables).map(([name, value]) => (
                        <div key={name}>
                          <Label className="text-xs text-gray-600">{`{{${name}}}`}</Label>
                          <Input
                            value={value}
                            onChange={(e) =>
                              setTestVariables((prev) => ({ ...prev, [name]: e.target.value }))
                            }
                            className="mt-1"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    onClick={handleSendTestEmail}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={sendingTestEmail}
                  >
                    {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
                  </Button>
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
                Close
              </Button>
              {hasPermission('templates', 'edit') && (
                <Button 
                  onClick={() => {
                    setShowPreviewDialog(false);
                    openEditDialog(selectedTemplate);
                  }}
                  variant="outline"
                >
                  <Edit2 size={14} className="mr-2" />
                  Edit Template
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Layout Dialog */}
      <Dialog open={showLayoutDialog} onOpenChange={setShowLayoutDialog}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-slate-50 to-blue-50/40">
            <DialogTitle className="text-3xl font-semibold tracking-tight text-slate-900">
              Edit Email Layout
            </DialogTitle>
            <p className="text-sm text-slate-600 mt-2">
              Compose reusable header/footer HTML and validate output instantly in the live canvas.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {['{{site_name}}', '{{support_email}}', '{{current_year}}', '{{logo_url}}'].map((token) => (
                <Badge key={token} variant="outline" className="bg-white text-slate-700 border-slate-200 font-mono text-xs">
                  {token}
                </Badge>
              ))}
            </div>
          </DialogHeader>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Code Editor</h3>
                  <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setLayoutEditMode('header')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        layoutEditMode === 'header'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Header
                    </button>
                    <button
                      type="button"
                      onClick={() => setLayoutEditMode('footer')}
                      className={`px-3 py-1.5 text-xs font-medium border-l border-slate-200 transition-colors ${
                        layoutEditMode === 'footer'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Footer
                    </button>
                  </div>
                </div>
                <div className="inline-flex border rounded-md overflow-hidden mb-3 hidden">
                  <button
                    type="button"
                    onClick={() => setLayoutEditMode('header')}
                    className={`px-3 py-1.5 text-xs ${
                      layoutEditMode === 'header'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Header
                  </button>
                  <button
                    type="button"
                    onClick={() => setLayoutEditMode('footer')}
                    className={`px-3 py-1.5 text-xs border-l ${
                      layoutEditMode === 'footer'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Footer
                  </button>
                </div>
                {layoutEditMode === 'header' && (
                  <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Header Background</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="color"
                        value={layoutForm.header_background_color || DEFAULT_HEADER_BACKGROUND_COLOR}
                        onChange={(e) => setLayoutForm({
                          ...layoutForm,
                          header_background_color: e.target.value
                        })}
                        className="h-9 w-12 border border-slate-300 rounded-md cursor-pointer bg-white"
                      />
                      <Input
                        value={layoutForm.header_background_color || DEFAULT_HEADER_BACKGROUND_COLOR}
                        onChange={(e) => setLayoutForm({
                          ...layoutForm,
                          header_background_color: e.target.value
                        })}
                        placeholder="#1a365d"
                        className="font-mono text-sm bg-white border-slate-300"
                      />
                    </div>
                  </div>
                )}
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  {layoutEditMode === 'header' ? 'Header HTML' : 'Footer HTML'}
                </Label>
                <Textarea
                  value={layoutEditMode === 'header' ? layoutForm.header_html : layoutForm.footer_html}
                  onChange={(e) => setLayoutForm({
                    ...layoutForm,
                    [layoutEditMode === 'header' ? 'header_html' : 'footer_html']: e.target.value
                  })}
                  rows={22}
                  className="mt-2 font-mono text-[13px] leading-6 border-slate-300 bg-slate-50/30 focus:bg-white transition-colors"
                  placeholder={`Paste ${layoutEditMode} HTML here`}
                />
              </div>
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Live Preview</h3>
                    <p className="text-xs text-slate-500 mt-1">Scale: {displayZoomPercent}%</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setPreviewDevice('desktop')}
                        className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                          previewDevice === 'desktop'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <Monitor size={14} />
                        Desktop
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewDevice('mobile')}
                        className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 border-l border-slate-200 transition-colors ${
                          previewDevice === 'mobile'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <Smartphone size={14} />
                        Mobile
                      </button>
                    </div>
                    <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setPreviewZoomMode('fit')}
                        className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                          previewZoomMode === 'fit'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        Fit
                      </button>
                      <select
                        value={previewZoomPercent}
                        onChange={(e) => {
                          setPreviewZoomMode('manual');
                          setPreviewZoomPercent(Number(e.target.value));
                        }}
                        className="px-2 py-1.5 text-xs border-l border-slate-200 bg-white text-slate-700"
                      >
                        {PREVIEW_ZOOM_OPTIONS.map((zoom) => (
                          <option key={zoom} value={zoom}>
                            {zoom}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div
                  ref={previewViewportRef}
                  className="border border-slate-200 rounded-lg bg-gradient-to-b from-slate-100 to-slate-50 p-3 h-[540px] overflow-auto"
                >
                  <div className="flex justify-center">
                    <div style={{ width: scaledPreviewWidth }}>
                      <div style={{ width: previewWidth, zoom: previewScale }}>
                        <div className="bg-white rounded-md overflow-hidden border shadow-sm">
                          <div
                            className="text-center"
                            style={{
                              backgroundColor: layoutForm.header_background_color || DEFAULT_HEADER_BACKGROUND_COLOR,
                              padding: '30px'
                            }}
                            dangerouslySetInnerHTML={{ __html: previewHeaderHtml }}
                          />
                          <div className="p-6 text-sm text-gray-700 leading-6">
                            <p className="font-medium mb-2">Sample Email Content</p>
                            <p className="mb-2">Dear John Doe,</p>
                            <p>This area represents the email template body content.</p>
                          </div>
                          <div
                            className="text-center border-t"
                            style={{ backgroundColor: '#f8f9fa', padding: '24px' }}
                            dangerouslySetInnerHTML={{ __html: previewFooterHtml }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-5 border-t border-slate-200">
              <Button variant="outline" className="border-slate-300" onClick={() => setShowLayoutDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleLayoutSave}
                className="bg-blue-600 hover:bg-blue-700 text-white min-w-[130px]"
                disabled={layoutSaving}
              >
                {layoutSaving ? 'Saving...' : 'Save Layout'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplates;
